import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAccount, useReadContract } from 'wagmi';
import { apiClient } from '@/shared/lib/apiClient';
import { dcaManagerAbi } from '@/shared/lib/abi/dcaManager';
import { CONTRACT_ADDRESS, SUPPORTED_TOKENS } from '@/shared/lib/chains';
import type { ScheduleDetails } from '@/shared/types/api';
import type { Address } from 'viem';
import {
  getScheduleFieldOverride,
  getScheduleOrderOverride,
  pruneScheduleOrderOverride,
  pruneScheduleOverride,
  ratchetScheduleBalanceOverride,
  ROOTSTOCK_FINALITY_STABILITY_MS,
  useScheduleFieldOverrides,
  useScheduleOrderOverrides,
  useScheduleTombstones,
  type OverrideField,
  type ScheduleFieldOverride,
  type ScheduleOrderOverride,
} from '../lib/scheduleActionStore';

export function useSchedulesFromApi(userAddress: Address | undefined) {
  return useQuery({
    queryKey: ['schedules', 'api', userAddress],
    queryFn: () => apiClient.schedules.list({ user: userAddress }),
    enabled: !!userAddress,
    staleTime: 30_000,
  });
}

export function useDeletedSchedules(
  userAddress: string | undefined,
) {
  return useQuery({
    queryKey: ['schedules', 'deleted', userAddress],
    queryFn: () => {
      if (userAddress == null) throw new Error('Address required');
      return apiClient.schedules.deletedList(userAddress);
    },
    enabled: !!userAddress,
    staleTime: 30_000,
  });
}

export function useSchedulesOnChain(
  tokenAddress: Address,
  account: Address | undefined,
  enabled = true,
) {
  return useReadContract({
    address: CONTRACT_ADDRESS,
    abi: dcaManagerAbi,
    functionName: 'getMyDcaSchedules',
    args: [tokenAddress],
    account,
    query: { enabled: enabled && !!account },
  });
}

function toLowerCaseAddress(value: string): string {
  return value.toLowerCase();
}

function extractScheduleId(entry: unknown): string | null {
  if (!entry || typeof entry !== 'object') return null;
  const maybeRecord = entry as Record<string | number, unknown>;
  const scheduleId = maybeRecord['scheduleId'] ?? maybeRecord[4];
  if (typeof scheduleId !== 'string') return null;
  return scheduleId.toLowerCase();
}

function extractBigIntField(entry: unknown, key: string, index: number): bigint | null {
  if (!entry || typeof entry !== 'object') return null;
  const maybeRecord = entry as Record<string | number, unknown>;
  const value = maybeRecord[key] ?? maybeRecord[index];
  return typeof value === 'bigint' ? value : null;
}

type OnChainSchedule = {
  scheduleId: string;
  token: Address;
  user: Address | undefined;
  balance: string;
  purchaseAmount: string;
  purchasePeriod: string;
  lastPurchaseTimestamp: string;
  lendingProtocolIndex: number;
};

function extractOnChainSchedule(
  entry: unknown,
  tokenAddress: Address,
  userAddress: Address | undefined,
): OnChainSchedule | null {
  const scheduleId = extractScheduleId(entry);
  if (!scheduleId) return null;

  const tokenBalance = extractBigIntField(entry, 'tokenBalance', 0);
  const purchaseAmount = extractBigIntField(entry, 'purchaseAmount', 1);
  const purchasePeriod = extractBigIntField(entry, 'purchasePeriod', 2);
  const lastPurchaseTimestamp = extractBigIntField(entry, 'lastPurchaseTimestamp', 3);
  const lendingProtocolIndex = extractBigIntField(entry, 'lendingProtocolIndex', 5);

  if (
    tokenBalance === null ||
    purchaseAmount === null ||
    purchasePeriod === null ||
    lastPurchaseTimestamp === null ||
    lendingProtocolIndex === null
  ) {
    return null;
  }

  return {
    scheduleId,
    token: tokenAddress,
    user: userAddress,
    balance: tokenBalance.toString(),
    purchaseAmount: purchaseAmount.toString(),
    purchasePeriod: purchasePeriod.toString(),
    lastPurchaseTimestamp: lastPurchaseTimestamp.toString(),
    lendingProtocolIndex: Number(lendingProtocolIndex),
  };
}

function toDisplaySchedule(onChain: OnChainSchedule, apiSchedule: ScheduleDetails | undefined): ScheduleDetails {
  if (!apiSchedule) {
    return {
      scheduleId: onChain.scheduleId,
      user: onChain.user ?? '0x0000000000000000000000000000000000000000',
      token: onChain.token,
      balance: onChain.balance,
      purchaseAmount: onChain.purchaseAmount,
      purchasePeriod: onChain.purchasePeriod,
      lastPurchaseTimestamp: onChain.lastPurchaseTimestamp,
      lendingProtocolIndex: onChain.lendingProtocolIndex,
      creationTimestamp: 0,
      lastUpdateTimestamp: 0,
      pendingIndexation: true,
    };
  }

  return {
    ...apiSchedule,
    balance: onChain.balance,
    purchaseAmount: onChain.purchaseAmount,
    purchasePeriod: onChain.purchasePeriod,
    lastPurchaseTimestamp: onChain.lastPurchaseTimestamp,
    lendingProtocolIndex: onChain.lendingProtocolIndex,
    pendingIndexation: false,
  };
}

const OVERRIDE_FIELDS: readonly OverrideField[] = ['balance', 'purchaseAmount', 'purchasePeriod'];

/**
 * Substitute confirmed-but-not-yet-visible values. Balance reconciliation is
 * directional so an intermediate stale snapshot cannot increase the amount a
 * user can withdraw; edited fields remain overridden until the exact value is
 * observed.
 */
function applyFieldOverrides(
  schedule: ScheduleDetails,
  overrides: Record<string, ScheduleFieldOverride>,
): ScheduleDetails {
  let result = schedule;
  for (const kind of OVERRIDE_FIELDS) {
    const override = getScheduleFieldOverride(overrides, schedule.scheduleId, kind);
    if (!override) continue;
    if (Date.now() - override.createdAt > ROOTSTOCK_FINALITY_STABILITY_MS) continue;
    const wouldRaiseBalanceLowWater =
      kind === 'balance' && BigInt(result.balance) > BigInt(override.expected);
    if (!hasSourceReconciled(result[kind], override) || wouldRaiseBalanceLowWater) {
      result = { ...result, [kind]: override.expected };
    }
  }
  return result;
}

function hasSourceReconciled(rawValue: string, override: ScheduleFieldOverride): boolean {
  if (override.kind !== 'balance') return rawValue === override.confirmedExpected;

  const raw = BigInt(rawValue);
  const preTx = BigInt(override.preTx);
  const expected = BigInt(override.confirmedExpected);

  if (expected < preTx) {
    // Withdrawal: a value above the receipt-block result may be an older
    // intermediate snapshot. Keep the lower authoritative value until the
    // source reaches it (or moves lower due to a later purchase).
    return raw <= expected;
  }
  if (expected > preTx) {
    // Net deposit: an intermediate value below the receipt-block result is
    // conservative, but a higher value can be a replica that has observed an
    // earlier deposit and not a later withdrawal. Never expose that inflation.
    return raw !== preTx && raw <= expected;
  }
  return raw === expected;
}

function resolveScheduleIndex(
  schedule: ScheduleDetails,
  onChainSchedules: readonly unknown[] | undefined,
  orderOverride: ScheduleOrderOverride | undefined,
): number | null {
  if (!onChainSchedules) return null;

  const targetId = schedule.scheduleId.toLowerCase();
  if (orderOverride) {
    const authoritativeIndex = orderOverride.scheduleIds.indexOf(targetId);
    if (authoritativeIndex >= 0) return authoritativeIndex;

    // A new schedule may have been appended after the delete. Its raw index is
    // safe only when the raw list starts with the entire receipt-block order.
    const rawIds = onChainSchedules.map(extractScheduleId);
    const hasAuthoritativePrefix = orderOverride.scheduleIds.every(
      (scheduleId, index) => rawIds[index] === scheduleId,
    );
    if (!hasAuthoritativePrefix) return null;
  }
  const index = onChainSchedules.findIndex((entry) => extractScheduleId(entry) === targetId);
  return index >= 0 ? index : null;
}

export function useAllUserSchedules() {
  const { address, chainId } = useAccount();
  const tombstones = useScheduleTombstones();
  const fieldOverrides = useScheduleFieldOverrides();
  const orderOverrides = useScheduleOrderOverrides();

  const apiQuery = useSchedulesFromApi(address);

  const docSchedules = useSchedulesOnChain(
    SUPPORTED_TOKENS[0].address,
    address,
    !!address,
  );

  const usdrifSchedules = useSchedulesOnChain(
    SUPPORTED_TOKENS[1].address,
    address,
    !!address,
  );

  const docTokenAddress = toLowerCaseAddress(SUPPORTED_TOKENS[0].address);
  const usdrifTokenAddress = toLowerCaseAddress(SUPPORTED_TOKENS[1].address);
  const apiSchedules = apiQuery.data?.schedules ?? [];
  const apiScheduleById = new Map(apiSchedules.map((s) => [s.scheduleId.toLowerCase(), s]));

  const docOnChainSchedules = (docSchedules.data ?? [])
    .map((entry) => extractOnChainSchedule(entry, SUPPORTED_TOKENS[0].address, address))
    .filter((schedule): schedule is OnChainSchedule => schedule !== null);
  const usdrifOnChainSchedules = (usdrifSchedules.data ?? [])
    .map((entry) => extractOnChainSchedule(entry, SUPPORTED_TOKENS[1].address, address))
    .filter((schedule): schedule is OnChainSchedule => schedule !== null);

  const docApiSchedules = apiSchedules.filter(
    (schedule) => toLowerCaseAddress(schedule.token) === docTokenAddress,
  );
  const usdrifApiSchedules = apiSchedules.filter(
    (schedule) => toLowerCaseAddress(schedule.token) === usdrifTokenAddress,
  );

  // Active schedule existence is chain-authoritative per token.
  // If a token's RPC call fails, fallback to API snapshot for that token only.
  const docActiveSchedules = docSchedules.error
    ? docApiSchedules
    : docOnChainSchedules.map((onChain) =>
        toDisplaySchedule(onChain, apiScheduleById.get(onChain.scheduleId.toLowerCase())),
      );
  const usdrifActiveSchedules = usdrifSchedules.error
    ? usdrifApiSchedules
    : usdrifOnChainSchedules.map((onChain) =>
        toDisplaySchedule(onChain, apiScheduleById.get(onChain.scheduleId.toLowerCase())),
      );

  const rawSchedules = [...docActiveSchedules, ...usdrifActiveSchedules];
  const isRpcFallbackActive = !!docSchedules.error || !!usdrifSchedules.error;

  // Confirmed deletes/mutations may not be visible yet (lagging RPC replicas,
  // ~6 min indexer finalization delay), so hide tombstoned schedules and apply
  // field overrides until fresh data reflects the transaction.
  const schedules = rawSchedules
    .filter((schedule) => !tombstones.has(schedule.scheduleId.toLowerCase()))
    .map((schedule) => applyFieldOverrides(schedule, fieldOverrides));

  // During the finality window, keep a conservative balance low-water mark.
  // Edited fields can retire as soon as a direct chain read reaches the value;
  // every field override retires once the Rootstock finality margin passes.
  const overrideReconcileKey = rawSchedules
    .map((s) => `${s.scheduleId}:${s.balance}:${s.purchaseAmount}:${s.purchasePeriod}`)
    .join('|');
  useEffect(() => {
    if (!address || chainId === undefined) return;
    for (const raw of rawSchedules) {
      for (const kind of OVERRIDE_FIELDS) {
        const override = getScheduleFieldOverride(fieldOverrides, raw.scheduleId, kind);
        if (!override) continue;
        if (Date.now() - override.createdAt > ROOTSTOCK_FINALITY_STABILITY_MS) {
          pruneScheduleOverride(chainId, address, raw.scheduleId, kind);
          continue;
        }
        const tokenAddress = toLowerCaseAddress(raw.token);
        const hasDirectChainSource =
          (tokenAddress === docTokenAddress && !docSchedules.error) ||
          (tokenAddress === usdrifTokenAddress && !usdrifSchedules.error);
        if (kind !== 'balance' && hasDirectChainSource && hasSourceReconciled(raw[kind], override)) {
          pruneScheduleOverride(chainId, address, raw.scheduleId, kind);
          continue;
        }
        if (
          kind === 'balance' &&
          raw.balance !== override.preTx &&
          BigInt(raw.balance) < BigInt(override.expected)
        ) {
          ratchetScheduleBalanceOverride(
            chainId,
            address,
            raw.scheduleId,
            raw.balance,
          );
        }
      }
    }
    // rawSchedules is rebuilt each render; overrideReconcileKey captures the relevant fields.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    overrideReconcileKey,
    fieldOverrides,
    address,
    chainId,
    docSchedules.error,
    usdrifSchedules.error,
    docTokenAddress,
    usdrifTokenAddress,
  ]);

  const orderReconcileKey = [
    ...(docSchedules.data ?? []).map(extractScheduleId),
    '|',
    ...(usdrifSchedules.data ?? []).map(extractScheduleId),
  ].join(':');
  useEffect(() => {
    if (!address || chainId === undefined) return;
    const tokenReads = [
      { token: docTokenAddress, data: docSchedules.data, error: docSchedules.error },
      { token: usdrifTokenAddress, data: usdrifSchedules.data, error: usdrifSchedules.error },
    ];
    for (const { token, data, error } of tokenReads) {
      if (error || data === undefined || !getScheduleOrderOverride(orderOverrides, token)) continue;
      const stillShowsDeletedSchedule = data.some((entry) => {
        const scheduleId = extractScheduleId(entry);
        return scheduleId !== null && tombstones.has(scheduleId);
      });
      if (!stillShowsDeletedSchedule) {
        pruneScheduleOrderOverride(chainId, address, token);
      }
    }
  }, [
    address,
    chainId,
    docSchedules.data,
    docSchedules.error,
    docTokenAddress,
    orderOverrides,
    orderReconcileKey,
    tombstones,
    usdrifSchedules.data,
    usdrifSchedules.error,
    usdrifTokenAddress,
  ]);

  const pendingSchedules = schedules.filter((schedule) => schedule.pendingIndexation === true);
  function getScheduleIndex(schedule: ScheduleDetails): number | null {
    const tokenAddress = toLowerCaseAddress(schedule.token);

    const resolveFromChain = (onChainSchedules: readonly unknown[] | undefined) => {
      if (!onChainSchedules) return null;
      const stillShowsDeletedSchedule = onChainSchedules.some((entry) => {
        const scheduleId = extractScheduleId(entry);
        return scheduleId !== null && tombstones.has(scheduleId);
      });
      const orderOverride = stillShowsDeletedSchedule
        ? getScheduleOrderOverride(orderOverrides, tokenAddress)
        : undefined;
      if (stillShowsDeletedSchedule && !orderOverride) return null;
      return resolveScheduleIndex(schedule, onChainSchedules, orderOverride);
    };

    if (tokenAddress === docTokenAddress) {
      return resolveFromChain(docSchedules.data);
    }

    if (tokenAddress === usdrifTokenAddress) {
      return resolveFromChain(usdrifSchedules.data);
    }

    return null;
  }

  return {
    apiSchedules,
    schedules,
    pendingSchedules,
    docOnChain: docSchedules.data ?? [],
    usdrifOnChain: usdrifSchedules.data ?? [],
    getScheduleIndex,
    // Wait for chain reads to settle before final active-list reconciliation.
    isLoading: apiQuery.isLoading || docSchedules.isLoading || usdrifSchedules.isLoading,
    error: apiQuery.error ?? null,
    isRpcFallbackActive,
    rpcErrors: {
      doc: docSchedules.error ?? null,
      usdrif: usdrifSchedules.error ?? null,
    },
    refetch: () => {
      void apiQuery.refetch();
      void docSchedules.refetch();
      void usdrifSchedules.refetch();
    },
  };
}

export function useContractLimits() {
  const minPeriod = useReadContract({
    address: CONTRACT_ADDRESS,
    abi: dcaManagerAbi,
    functionName: 'getMinPurchasePeriod',
  });

  const maxSchedules = useReadContract({
    address: CONTRACT_ADDRESS,
    abi: dcaManagerAbi,
    functionName: 'getMaxSchedulesPerToken',
  });

  const defaultMinAmount = useReadContract({
    address: CONTRACT_ADDRESS,
    abi: dcaManagerAbi,
    functionName: 'getDefaultMinPurchaseAmount',
  });

  return {
    minPurchasePeriod: minPeriod.data,
    maxSchedulesPerToken: maxSchedules.data,
    defaultMinPurchaseAmount: defaultMinAmount.data,
    isLoading: minPeriod.isLoading || maxSchedules.isLoading || defaultMinAmount.isLoading,
  };
}
